/**
 * Moons computed live by astronomy-engine (Concept §5: "Planets, Moon, Galilean moons — astronomy-engine live"): the
 * Galilean moons and, since S4-06, Earth's Moon. Positions are relative to their planet's centre, km, ecliptic J2000.
 */
import { GeoMoon, JupiterMoons } from 'astronomy-engine';
import { eqjToEcliptic } from './ephemeris';
import { hermiteAt } from './hermite';
import { tdbDaysToAstroTime } from './time';
import { AU_KM } from './units';
import type { Vec3 } from './vec3';

export const ANALYTIC_MOONS = ['io', 'europa', 'ganymede', 'callisto', 'moon'] as const;
export type AnalyticMoonId = (typeof ANALYTIC_MOONS)[number];

export function isAnalyticMoon(id: string): id is AnalyticMoonId {
  return (ANALYTIC_MOONS as readonly string[]).includes(id);
}

/** Writes every analytic moon's position relative to its planet (km, ecliptic J2000) into `out`. */
export function analyticMoons(tdbDays: number, out: Record<AnalyticMoonId, Vec3>): void {
  const time = tdbDaysToAstroTime(tdbDays);
  const info = JupiterMoons(time);
  for (const id of ['io', 'europa', 'ganymede', 'callisto'] as const) {
    const s = info[id];
    eqjToEcliptic(out[id], s.x, s.y, s.z, AU_KM);
  }
  const m = GeoMoon(time);
  eqjToEcliptic(out.moon, m.x, m.y, m.z, AU_KM);
}

/** One analytic moon's position relative to its planet (km, ecliptic J2000): only that moon is computed. */
export function analyticMoonOffset(id: AnalyticMoonId, tdbDays: number, out: Vec3): Vec3 {
  const time = tdbDaysToAstroTime(tdbDays);
  if (id === 'moon') {
    const m = GeoMoon(time);
    return eqjToEcliptic(out, m.x, m.y, m.z, AU_KM);
  }
  const s = JupiterMoons(time)[id];
  return eqjToEcliptic(out, s.x, s.y, s.z, AU_KM);
}

/** Jupiter's four moons only (km, ecliptic J2000, relative to Jupiter): without computing Earth's Moon as well. */
export function galileanMoons(tdbDays: number, out: Record<'io' | 'europa' | 'ganymede' | 'callisto', Vec3>): void {
  const info = JupiterMoons(tdbDaysToAstroTime(tdbDays));
  for (const id of ['io', 'europa', 'ganymede', 'callisto'] as const) {
    const s = info[id];
    eqjToEcliptic(out[id], s.x, s.y, s.z, AU_KM);
  }
}

/**
 * Earth's Moon relative to Earth (km, ecliptic J2000) from a cubic Hermite curve between hourly nodes, for bulk
 * lookups — trail sampling evaluates it for every line sample near Earth, and the full lunar theory (`GeoMoon`) took
 * a fifth of a phone's frame at rest (S4-15). Off by less than a metre (unit test), so a trail 100 km above the Moon
 * does not wobble.
 */
export class MoonTrack {
  /** Node spacing, days. */
  static readonly STEP = 1 / 24;
  /** Node spacing of a coarse track (quick distance tests, S4-15), days, and its error bound, km. */
  static readonly COARSE_STEP = 1 / 4;
  static readonly COARSE_ERROR_KM = 100;

  constructor(private readonly step = MoonTrack.STEP) {}
  private readonly nodes = new Map<number, Float64Array>();
  private readonly t = [0, 0];
  private readonly s = new Float64Array(12);
  private readonly a = { x: 0, y: 0, z: 0 };
  private readonly b = { x: 0, y: 0, z: 0 };

  at(tdbDays: number, out: Vec3): Vec3 {
    const k = Math.floor(tdbDays / this.step);
    this.s.set(this.node(k), 0);
    this.s.set(this.node(k + 1), 6);
    this.t[0] = k * this.step;
    this.t[1] = (k + 1) * this.step;
    hermiteAt(this.t, this.s, 0, tdbDays, out);
    return out;
  }

  /** Position (km) and velocity (km/day, central difference over ±30 s) at node `k`. */
  private node(k: number): Float64Array {
    let n = this.nodes.get(k);
    if (n) return n;
    if (this.nodes.size > 50_000) this.nodes.clear(); // years of hours; a long jump starts afresh
    const t = k * this.step;
    const h = 30 / 86_400;
    const p = analyticMoonOffset('moon', t, { x: 0, y: 0, z: 0 });
    analyticMoonOffset('moon', t + h, this.a);
    analyticMoonOffset('moon', t - h, this.b);
    const f = 1 / (2 * h);
    n = Float64Array.of(p.x, p.y, p.z, (this.a.x - this.b.x) * f, (this.a.y - this.b.y) * f, (this.a.z - this.b.z) * f);
    this.nodes.set(k, n);
    return n;
  }
}

/** Parent planet of each analytic moon. */
export const ANALYTIC_MOON_PARENT: Record<AnalyticMoonId, 'earth' | 'jupiter'> = {
  io: 'jupiter',
  europa: 'jupiter',
  ganymede: 'jupiter',
  callisto: 'jupiter',
  moon: 'earth',
};

/** Sidereal orbital periods, days (analytic moons and the moons from HORIZONS tables). */
export const MOON_PERIOD_DAYS: Record<string, number> = {
  moon: 27.321661,
  io: 1.769138,
  europa: 3.551181,
  ganymede: 7.154553,
  callisto: 16.689018,
  titan: 15.945421,
};

/**
 * Gravitational parameters of the moons with nested bubbles, km³/s² (JPL satellite ephemerides): whether a spacecraft
 * is *bound* to a moon (in orbit around it, Apollo) or only passes it (Voyager at Ganymede, Cassini at Titan).
 */
export const MOON_GM: Readonly<Record<string, number>> = {
  moon: 4902.8,
  io: 5959.92,
  europa: 3202.74,
  ganymede: 9887.83,
  callisto: 7179.29,
  titan: 8978.14,
};

/** Whether a state relative to a moon (km, km/day) is bound to it: negative orbital energy. */
export function boundToMoon(moon: string, q: Vec3, wKmPerDay: Vec3): boolean {
  const gm = MOON_GM[moon];
  if (gm === undefined) return false;
  const v = Math.hypot(wKmPerDay.x, wKmPerDay.y, wKmPerDay.z) / 86_400;
  return (v * v) / 2 - gm / Math.hypot(q.x, q.y, q.z) < 0;
}

/** Angular speed on screen (degrees of orbit per real second) from which a moon starts to dissolve into its ring. */
export const MOON_BLUR_START_DEG_S = 90;
/** … and at which only the ring is left. */
export const MOON_BLUR_FULL_DEG_S = 270;

/**
 * How far a moon has "blurred" into its orbit ring (0 = solid moon, 1 = ring only) when playback runs at
 * `speedDaysPerS` mission days per real second. Fast moons would otherwise jump around the planet stroboscopically
 * (maintainer feedback, Sprint 2): above ~90°/s the eye cannot follow single positions any more.
 */
export function moonBlur(periodDays: number, speedDaysPerS: number): number {
  const degPerS = (360 / periodDays) * Math.abs(speedDaysPerS);
  const x = (degPerS - MOON_BLUR_START_DEG_S) / (MOON_BLUR_FULL_DEG_S - MOON_BLUR_START_DEG_S);
  const c = Math.min(Math.max(x, 0), 1);
  return c * c * (3 - 2 * c);
}
